import { cn } from '@/lib/utils'
import { useIsPhone } from '@/hooks/useIsPhone'
import { Badge } from '../Badge/Badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Tooltip/Tooltip'
import { multiplePositionsDescription } from './multiplePositionsDescription'

export interface MultiplePositionsBadgeProps {
  /** The code's max_positions (per side); null when the code did not say. */
  maxPositions?: number | null
  /** The engine API level the code needs; 3 or more means a desktop 0.4+ requirement. */
  engineApi?: number | null
  className?: string
}

/**
 * "Multiple positions": the code keeps several positions open at once (engine API 3,
 * `position_mode = 'hedging'`), so running it live needs a hedging MetaTrader 5 account.
 *
 * Shown next to the bot page's Deploy control and on the Live page's rows, only for such code —
 * a single-position strategy wears nothing. Users read "multiple positions", never "hedging
 * mode"; the account requirement is worded with MT5's own label.
 */
export function MultiplePositionsBadge({ maxPositions = null, engineApi = null, className }: MultiplePositionsBadgeProps) {
  const phone = useIsPhone()
  const description = multiplePositionsDescription(maxPositions, engineApi)
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            tabIndex={0}
            aria-label={description}
            className={cn('text-warning border-warning/40 cursor-default', className)}
          >
            {phone ? 'Multi' : 'Multiple positions'}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-72">{description}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default MultiplePositionsBadge
